import React, { useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from 'src/containers';
import PrivateRoute from './components/private-route';
import { isUserLoggedIn } from './redux/action/auth.action';
import { RootState } from './redux/reducers';
import { authRoutesData, routesData } from './setup/route';
import SnakeGame from './components/SnakeGame';
import AuthLayout from './containers/auth-layout';
import { socketConnected } from './redux/action/socket.action';
import { NoResult } from './assets/images';

const adminRoutes = routesData.filter((route) => route.adminOnly === true);
const salesRoutes = routesData.filter((route) => route.adminOnly === false);

interface IProps {}
const App: React.FC<IProps> = ({}) => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);

  // Get the value of the 'refCode' parameter
  const refCode = searchParams.get('refCode');
  const dispatch: any = useDispatch();
  const location = useLocation();
  const dashLayoutHeading = useSelector(
    (state: RootState) => state.dashLayoutHeading.dashLayoutHeading
  );

  const auth = useSelector(({ authReducer }: RootState) => authReducer);

  React.useEffect(() => {
    if (!auth.authenticate) {
      dispatch(isUserLoggedIn());
    }
  }, [auth.authenticate]);

  const { shiftData } = useSelector(({ shiftData }: RootState) => shiftData);

  React.useEffect(() => {
    if (!auth?.authenticate) {
      return !location.pathname.includes('/register')
        ? navigate('/login')
        : navigate(
            `${location.pathname}${refCode ? '?refCode=' + refCode : ''}`
          );
    } else if (
      auth.role !== 'admin' &&
      (location.pathname === '/login' || location.pathname === '/register')
    ) {
      return navigate('/dine-in');
    } else if (
      auth.role === 'admin' &&
      (location.pathname === '/login' ||
        location.pathname === '/register' ||
        location.pathname === '/forget-password' ||
        location.pathname === '/otp' ||
        location.pathname === '/reset-password')
    ) {
      return navigate('/');
    } else {
      return navigate(location.pathname);
    }
  }, [auth]);

  const { socket } = useSelector(({ socketData }: RootState) => socketData);

  React.useEffect(() => {
    return () => {
      if (socket) {
        socket.emit('disconnection', function () {
          dispatch(socketConnected(null));
        });
      }
    };
  }, [socket]);

  React.useEffect(() => {
    if (dashLayoutHeading) {
      document.title = dashLayoutHeading;
    }
  }, [dashLayoutHeading]);

  // const ignoreRoute = [ 'SingleOrder'];
  // function isHeadingIncluded(heading) {
  //   return ignoreRoute.includes(heading);
  // }

  const routes =
    auth?.authenticate && auth.role === 'admin' ? adminRoutes : salesRoutes;

  useEffect(() => {
    const isRoutePresent = routes.some(
      (route) => route.path === location.pathname
    );
    if (!isRoutePresent) {
      const timer = setTimeout(() => {
        navigate(`${auth.role === 'admin' ? '/' : '/dine-in'}`);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [routes]);

  useLayoutEffect(() => {
    const acessToken = localStorage.getItem('accessToken');
    const now = new Date();

    const isExpire: boolean =
      now < new Date(Number(localStorage.getItem('expiresAt')));

    if (acessToken && isExpire) {
      navigate('/');
    } else {
      navigate('/login');
    }
  }, []);

  const Page404 = () => (
    <div
      className={`w-full flex flex-col gap-8 justify-center items-center text-2xl ${
        auth.role === 'admin' ? 'h-full' : 'h-screen'
      } text-black`}
    >
      <div className="w-[351px] h-[285px] flex items-center justify-center">
        <img src={NoResult} alt="noResults" />
      </div>
      <strong className="text-6xl"> Page not found</strong>
      Redirecting.....
    </div>
  );

  return (
    <Routes>
      {routes.map((e) => (
        <Route
          element={<PrivateRoute route={e} shiftData={shiftData} />}
          key={e?.id}
        >
          {!e?.Lock && (
            <Route
              element={
                <DashboardLayout
                  heading={dashLayoutHeading || e?.heading}
                  icon={e?.icon}
                  title={e?.title}
                  width={e?.width}
                >
                  {e?.element}
                </DashboardLayout>
              }
              path={e?.path}
            />
          )}
        </Route>
      ))}

      {authRoutesData.map((element: any) => (
        <Route
          key={element?.id}
          element={
            <AuthLayout title={element?.title}>{element?.element}</AuthLayout>
          }
          path={element?.path}
        />
      ))}

      <Route
        path="*"
        element={
          auth.role === 'admin' ? (
            <DashboardLayout heading={'404 Not Found'} width={'full'}>
              <Page404 />
            </DashboardLayout>
          ) : (
            <Page404 />
          )
        }
      />
    </Routes>
  );
};

export default React.memo(App);
